import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { CasingSearchService } from './casing-search.services';
import { AutocompleteOption, DropdownData } from '../model/casing-auto-complete.model';
import {
  CasingDropdownDataState,
  CasingDropdownDataStateModel,
  UpdateCasingDropdownDataAction
} from 'src/app/shared/state/CasingDropdownState';
import { ProductsState, FetchAllProductsAction } from 'src/app/shared/state/ProductsState';

@Injectable({
  providedIn: 'root'
})
export class CasingDropdownDataService {
  public store = inject(Store);
  public casingSearchService = inject(CasingSearchService);
  public translationService = inject(TranslationService);
  public alertService = inject(AlertService);

  getCasingMakeList(): Observable<string[]> {
    const makes: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.makes);
    if (makes && makes.length > 0) {
      return of(makes.map((item) => this.formatMake(item)));
    }
    return this.casingSearchService.retrieveCasingsMake().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction('casingMakeListData', response.data));
        return response.data.map((item) => this.formatMake(item));
      }),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing makes'))
    );
  }

  getCasingSeriesList(): Observable<string[]> {
    const series: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.series);
    if (series && series.length > 0) {
      return of(series.map((item) => this.translationService.translate(item.code)));
    }
    return this.casingSearchService.retrieveCasingSeries().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction('casingSeriesListData', response.data));
        return this.translationService.translateList(response.data.map((item: AutocompleteOption) => item.code));
      }),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing series'))
    );
  }

  getCasingSizeList(): Observable<string[]> {
    const sizes: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.sizes);
    if (sizes && sizes.length > 0) {
      return of(sizes.map((item) => this.translationService.translate(item.code)));
    }
    return this.casingSearchService.retrieveCasingSizes().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction('casingSizeListData', response.data));
        return response.data.map((item: AutocompleteOption) => this.translationService.translate(item.code));
      }),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing sizes'))
    );
  }

  getCasingTreadList(): Observable<string[]> {
    const treads: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.treads);
    if (treads && treads.length > 0) {
      return of(treads.map((item) => this.translationService.translate(item.code)));
    }
    return this.casingSearchService.retrieveCasingTreads().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction('casingTreadListData', response.data));
        return response.data.map((item: AutocompleteOption) => this.translationService.translate(item.code));
      }),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing treads'))
    );
  }

  getCasingTreadWidthList(): Observable<string[]> {
    const widths: Array<AutocompleteOption> = this.store.selectSnapshot(CasingDropdownDataState.treadWidths);
    if (widths && widths.length > 0) {
      return of(widths.map((item) => this.translationService.translate(item.code)));
    }
    return this.casingSearchService.retrieveCasingtreadWidths().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction('casingTreadWidthListData', response.data));
        return response.data.map((item: AutocompleteOption) => this.translationService.translate(item.code));
      }),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing treadwidths'))
    );
  }

  getCasingGradeList(): Observable<string[]> {
    const casingGrades: Array<string> = this.store.selectSnapshot(ProductsState.products)?.casingGrade;
    if (casingGrades && casingGrades.length > 0) {
      return of(this.translationService.translateList(casingGrades));
    }
    return this.store.dispatch(new FetchAllProductsAction()).pipe(
      map(() => this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).casingGrade)),
      catchError((error) => this.handleError(error, 'An error occurred while retrieving casing grades'))
    );
  }

  private formatMake(item: AutocompleteOption): string {
    return `${this.translationService.translate(item.code)} - ${this.translationService.translate(item.description)}`;
  }

  getMakeUuidFromText(text: string): Observable<string | undefined> {
    return this.resolveUuid(
      'casingMakeListData',
      this.store.selectSnapshot(CasingDropdownDataState.makes),
      () => this.casingSearchService.retrieveCasingsMake(),
      text
    );
  }

  getSeriesUuidFromText(text: string): Observable<string | undefined> {
    return this.resolveUuid(
      'casingSeriesListData',
      this.store.selectSnapshot(CasingDropdownDataState.series),
      () => this.casingSearchService.retrieveCasingSeries(),
      text
    );
  }

  getSizeUuidFromText(text: string): Observable<string | undefined> {
    return this.resolveUuid(
      'casingSizeListData',
      this.store.selectSnapshot(CasingDropdownDataState.sizes),
      () => this.casingSearchService.retrieveCasingSizes(),
      text
    );
  }

  getTreadUuidFromText(text: string): Observable<string | undefined> {
    return this.resolveUuid(
      'casingTreadListData',
      this.store.selectSnapshot(CasingDropdownDataState.treads),
      () => this.casingSearchService.retrieveCasingTreads(),
      text
    );
  }

  private resolveUuid(
    fieldName: keyof CasingDropdownDataStateModel,
    cached: Array<AutocompleteOption>,
    fetch: () => Observable<DropdownData>,
    text: string
  ): Observable<string | undefined> {
    const normalized = text?.trim().toLowerCase();
    if (!normalized) {
      return of(undefined);
    }
    if (cached && cached.length > 0) {
      return of(this.matchUuid(cached, normalized));
    }
    return fetch().pipe(
      map((response) => {
        this.store.dispatch(new UpdateCasingDropdownDataAction(fieldName, response.data));
        return this.matchUuid(response.data, normalized);
      }),
      catchError(() => of(undefined))
    );
  }

  private matchUuid(options: Array<AutocompleteOption>, normalized: string): string | undefined {
    const match = options.find(
      (option) =>
        option.code?.toLowerCase() === normalized ||
        option.description?.toLowerCase() === normalized ||
        option.label?.toLowerCase() === normalized ||
        (!!option.code && !!option.description && `${option.code} - ${option.description}`.toLowerCase() === normalized)
    );
    return match?.id;
  }

  private handleError(error: any, fallbackMessage: string): Observable<string[]> {
    this.alertService.showAlert({
      type: AlertType.strong,
      content: error?.message || fallbackMessage
    } as Alert);
    return of<string[]>([]);
  }
}
